import { DeepBoxError } from '@/models'
import DeepErrorHandlerService from '@/services/DeepErrorHandlerService.ts'
import { DEEP_BOX_ERROR_CODES } from '@/types/error.ts'
import { createApiHttpClient } from '@deepcloud/api'
import { createDeepAAPI } from '@deepcloud/api-deep-a'
import { createDeepAdminAPI } from '@deepcloud/api-deep-admin'
import { createDeepAdminBackendAPI } from '@deepcloud/api-deep-admin-backend'
import { createDeepBoxAPI } from '@deepcloud/api-deep-box'
import { createDeepOAPI } from '@deepcloud/api-deep-o'
import { createDeepSignAPI } from '@deepcloud/api-deep-sign'
import { createDeepValidatorAPI } from '@deepcloud/api-deep-validator'
import { useKeycloak } from './auth/keycloak'
import type { ForbiddenError } from '@deepcloud/api-deep-box'
import type { AxiosError } from 'axios'

// runs on every deepBoxAPI response, so the elevated-auth-required case is
// surfaced once here instead of being duplicated in each store/component
// that calls the API
function handleDeepBoxApiResponseError(error: AxiosError<ForbiddenError>) {
  if (
    error.response?.status === 403 &&
    error.response.data?.messageId === 'elevated.auth.required'
  ) {
    DeepErrorHandlerService.error(
      new DeepBoxError(undefined, {
        statusCode: 403,
        errorCode: DEEP_BOX_ERROR_CODES.ELEVATED_AUTH_REQUIRED,
      }),
    )
  }

  return Promise.reject(error)
}

export const useApi = createGlobalState(() => {
  const { bearer } = useKeycloak()

  const { locale } = useI18n()

  const deepBoxAPI = computed(() => {
    const api = createDeepBoxAPI({
      baseUrl: import.meta.env.VITE_DEEPBOX_API_BASE_URL,
      adapter: 'axios',
      token: () => bearer.value,
      lang: () => locale.value,
    })

    api.deepBoxHttpClient.instance.interceptors.response.use(
      (response) => response,
      handleDeepBoxApiResponseError,
    )

    return api
  })

  const deepAAPI = computed(() =>
    createDeepAAPI({
      baseUrl: `${import.meta.env.VITE_DEEPA_API_BASE_URL}/api`,
      adapter: 'axios',
      token: () => bearer.value,
      lang: () => locale.value,
    }),
  )

  const deepSignAPI = computed(() =>
    createDeepSignAPI({
      baseUrl: import.meta.env.VITE_DEEPSIGN_API_BASE_URL,
      adapter: 'axios',
      token: () => bearer.value,
      lang: () => locale.value,
    }),
  )

  const deepAdminAPI = computed(() =>
    createDeepAdminAPI({
      baseUrl: import.meta.env.VITE_DEEPADMIN_API_BASE_URL,
      adapter: 'axios',
      token: () => bearer.value,
      lang: () => locale.value,
    }),
  )

  const deepAdminBackendAPI = computed(() =>
    createDeepAdminBackendAPI({
      baseUrl: import.meta.env.VITE_DEEPADMIN_BACKEND_API_BASE_URL,
      adapter: 'axios',
      token: () => bearer.value,
      lang: () => locale.value,
    }),
  )

  const deepOAPI = computed(() =>
    createDeepOAPI({
      baseUrl: import.meta.env.VITE_DEEPO_API_BASE_URL,
      adapter: 'axios',
      token: () => bearer.value,
      lang: () => locale.value,
    }),
  )

  const deepStorageAPI = computed(() =>
    createApiHttpClient(
      import.meta.env.VITE_DEEPCLOUD_STORAGE_BASE_URL,
      'axios',
      undefined, // This API should be PUBLIC
      () => locale.value,
    ),
  )

  const deepValidatorAPI = computed(() =>
    createDeepValidatorAPI({
      baseUrl: import.meta.env.VITE_DEEPVALIDATOR_API_BASE_URL,
      adapter: 'axios',
      token: () => bearer.value,
      lang: () => locale.value,
    }),
  )

  return {
    deepBoxAPI,
    deepAAPI,
    deepSignAPI,
    deepAdminAPI,
    deepAdminBackendAPI,
    deepOAPI,
    deepStorageAPI,
    deepValidatorAPI,
  }
})
